import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { Suspense, lazy, useEffect, useLayoutEffect } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { Outlet, Route, useNavigate, useParams, } from 'react-router-dom';
import { userApi } from 'api';
import { useCheckNewRewards, useLocation, useOpen, useQueryString, useSession, } from 'hooks';
import i18n from 'i18n';
import { PersonalInfo, Referrer, TokenGating, Vas } from 'pages';
import AnteroomSerie from 'pages/AnteroomSerie';
import AnteroomShorts from 'pages/AnteroomShorts';
import Creator from 'pages/Creator';
import FavoriteCreators from 'pages/FavoriteCreators';
import Home from 'pages/Home';
import { SentryRoutes } from 'utils/sentry';
import { useModalAuth, useStore } from 'stores';
import { ErrorBoundery, ModalAuth, ModalChallenges, } from 'ui';
import pkgJson from '../package.json';
import GlobalStyles from './styles/globalStyles';
var Flixx = lazy(function () { return import('./pages/Flixx'); });
var Faqs = lazy(function () { return import('./pages/Faqs'); });
var Creators = lazy(function () { return import('./pages/Creators'); });
var Profile = lazy(function () { return import('./pages/Profile'); });
var NotFound = lazy(function () { return import('./pages/NotFound'); });
var Maintenance = lazy(function () { return import('./pages/Maintenance'); });
var Redirect = lazy(function () { return import('./pages/Redirect'); });
var AppRedirect = lazy(function () { return import('./pages/AppRedirect'); });
var Privacy = lazy(function () { return import('./pages/Privacy'); });
var LayoutCreators = lazy(function () { return import('./templates/LayoutCreators'); });
var LayoutPlay = lazy(function () { return import('./templates/LayoutPlay'); });
var ModalDeleteAccount = lazy(function () { return import('./ui/ModalDeleteAccount'); });
var ModalDownloadApp = lazy(function () { return import('./ui/ModalDownloadApp'); });
var PrivateRoute = lazy(function () { return import('./ui/PrivateRoute'); });
export var LangManager = function () {
    var _a = useParams().lang, lang = _a === void 0 ? '' : _a;
    var _b = useLocation(), pathname = _b.pathname, search = _b.search, state = _b.state;
    var navigate = useNavigate();
    var LANGS = ['es', 'en'];
    var isValidLang = LANGS.includes(lang);
    var path = isValidLang
        ? pathname.replace("/".concat(lang), '')
        : pathname;
    useLayoutEffect(function () {
        var newPath = "/".concat(i18n.language).concat(path).concat(search);
        if (pathname === newPath)
            return;
        navigate(newPath, { state: state, replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language, pathname]);
    return _jsx(Outlet, {});
};
var SuspenseWrapper = function (_a) {
    var children = _a.children;
    return (_jsx(React.Suspense, { fallback: null, children: children }));
};
var App = function () {
    var me = userApi.useGetMe().data;
    var _a = useLocation(), isPlay = _a.isPlay, pathname = _a.pathname;
    var _b = useSession(), isLoggedIn = _b.isLoggedIn, isPreLoggedIn = _b.isPreLoggedIn, isFreshLogIn = _b.isFreshLogIn;
    var isSeriesCreator = useStore().isSeriesCreator;
    var _c = useQueryString(), from = _c.from, action = _c.action, view = _c.view, remove = _c.remove;
    var isViewChallenges = view === 'challenges';
    var _d = useModalAuth(), openWelcome = _d.openWelcome, openValidate = _d.openValidate;
    var modalDownloadApp = useOpen();
    var modalDeleteAccount = useOpen();
    var modalChallenges = useOpen();
    var checkNewRewards = useCheckNewRewards().checkNewRewards;
    useEffect(function () {
        if (!isFreshLogIn)
            return;
        checkNewRewards();
    }, [isFreshLogIn]);
    // TODO: Maybe this could be in Session provider...
    useEffect(function () {
        if (isPreLoggedIn && !(me === null || me === void 0 ? void 0 : me.isConfirmed))
            openValidate();
    }, [isLoggedIn, isPreLoggedIn]);
    /**
     * Generates an abValue between 0 and 100
     */
    var generateAbValue = function () {
        var currentValue = localStorage.getItem('abValue');
        // It works because "0" is true jeje
        if (currentValue)
            return;
        var decimal = Math.random();
        var value = Math.floor(decimal * 101);
        localStorage.setItem('abValue', String(value));
    };
    useEffect(function () {
        if (from === 'webview')
            return;
        console.log("Flixxo Web v".concat(pkgJson.version));
        generateAbValue();
        if (location.pathname.includes('/vas/'))
            return;
        if (isMobileOnly && !isLoggedIn) {
            setTimeout(function () {
                modalDownloadApp.open();
            }, 3000);
        }
    }, []);
    useEffect(function () {
        if (action === 'delete') {
            if (!isLoggedIn) {
                openWelcome();
            }
            else {
                modalDeleteAccount.open();
            }
        }
    }, [isLoggedIn]);
    useEffect(function () {
        if (!modalChallenges.isOpen && isLoggedIn && isViewChallenges) {
            modalChallenges.open();
        }
        if (modalChallenges.isOpen && !isViewChallenges) {
            modalChallenges.close();
            remove('view');
        }
    }, [isViewChallenges, isLoggedIn, pathname]);
    useLayoutEffect(function () {
        window.scrollTo({ top: 0 });
    }, [pathname]);
    return (_jsxs(_Fragment, { children: [_jsx(GlobalStyles, { "$isPlay": isPlay }), _jsxs(ErrorBoundery, { children: [_jsxs(Suspense, { fallback: null, children: [modalChallenges.isOpen && (_jsx(ModalChallenges, { close: function () {
                                    modalChallenges.close();
                                    remove('view');
                                } })), modalDownloadApp.isOpen && (_jsx(ModalDownloadApp, { close: modalDownloadApp.close })), modalDeleteAccount.isOpen && (_jsx(ModalDeleteAccount, { close: modalDeleteAccount.close })), _jsx(ModalAuth, {})] }), _jsxs(SentryRoutes, { children: [_jsx(Route, { path: '/', element: _jsx(LangManager, {}) }), _jsx(Route, { path: 'app', element: (_jsx(SuspenseWrapper, { children: _jsx(AppRedirect, {}) })) }), _jsx(Route, { path: 'redirect', element: (_jsx(SuspenseWrapper, { children: _jsx(Redirect, {}) })) }), _jsx(Route, { path: 'maintenance', element: (_jsx(SuspenseWrapper, { children: _jsx(Maintenance, {}) })) }), _jsxs(Route, { path: '/:lang?', element: _jsx(LangManager, {}), children: [isSeriesCreator && (_jsxs(Route, { path: 'creators', element: (_jsx(SuspenseWrapper, { children: _jsx(PrivateRoute, { component: _jsx(LayoutCreators, {}) }) })), children: [_jsx(Route, { index: true, element: _jsx(Creators, {}) }), _jsx(Route, { path: '*', element: _jsx(NotFound, {}) })] })), _jsxs(Route, { element: (_jsx(SuspenseWrapper, { children: _jsx(LayoutPlay, {}) })), children: [_jsx(Route, { index: true, element: _jsx(Home, {}) }), _jsx(Route, { path: 'vas/:token', element: _jsx(Vas, {}) }), _jsx(Route, { path: 'referrer/:nickname', element: _jsx(Referrer, {}) }), _jsx(Route, { path: 'creator/:nickname', element: _jsx(Creator, {}) }), _jsx(Route, { path: 'series/:serieUUID/:seasonOrder?/:episodeOrder?', element: _jsx(AnteroomSerie, {}) }), _jsx(Route, { path: 'content/:contentUUID', element: _jsx(AnteroomShorts, {}) }), _jsx(Route, { path: 'profile', element: _jsx(PrivateRoute, { component: _jsx(Profile, {}) }) }), _jsx(Route, { path: 'personal', element: _jsx(PrivateRoute, { component: _jsx(PersonalInfo, {}) }) }), _jsx(Route, { path: 'favorites', element: _jsx(PrivateRoute, { component: _jsx(FavoriteCreators, {}) }) }), _jsx(Route, { path: 'token-gating', element: _jsx(TokenGating, {}) }), _jsx(Route, { path: 'flixx', element: _jsx(Flixx, {}) }), _jsx(Route, { path: 'faqs', element: _jsx(Faqs, {}) }), _jsx(Route, { path: 'privacy', element: _jsx(Privacy, {}) }), _jsx(Route, { path: '*', element: _jsx(NotFound, {}) })] })] })] })] })] }));
};
export default App;
